import api from '@/api';
import type { PaginationLinks, PaginationMeta, PaginationParams } from '@/types/api/common';
import type {
    ConvertPoints,
    Customer,
    KaibiganPointsResponse,
    LatestRedeemedReward,
    RedeemRewardData,
    RedemptionHistory,
    RedemptionRequestData,
    Reward,
} from '@/types/reward';
import { ApiFactory } from './base/ApiFactory';

/**
 * Service class for interacting with the Rewards API endpoints.
 * Handles reward listing, kaibigan points, and redemption operations.
 */
class RewardService extends ApiFactory<Reward> {
    /**
     * Creates an instance of RewardService.
     */
    constructor() {
        super('/rewards', api);
    }

    /**
     * Gets the latest featured rewards (up to 8 items).
     * @returns Promise with latest rewards response
     */
    async getLatestRewards(): Promise<Reward[]> {
        return this.get<Reward[]>(`${this.resource}/latest`);
    }

    /**
     * Gets a paginated list of all available rewards.
     * @param params - Pagination parameters
     * @returns Promise with paginated rewards response
     */
    async getAllRewards(params?: PaginationParams): Promise<{
        data: Reward[];
        links: PaginationLinks;
        meta: PaginationMeta;
    }> {
        return this.get<{
            data: Reward[];
            links: PaginationLinks;
            meta: PaginationMeta;
        }>(`${this.resource}/view-all`, params);
    }

    /**
     * Gets the customer's kaibigan points balance and related information.
     * @returns Promise with kaibigan points response
     */
    async getKaibiganPoints(): Promise<{ data: KaibiganPointsResponse }> {
        return this.get<{ data: KaibiganPointsResponse }>(`${this.resource}/kaibigan-points`);
    }

    /**
     * Submits a request to redeem a reward.
     * @param data - Redemption request data containing reward_id and optional notes
     * @returns Promise with redemption history response
     */
    async requestRedeem(
        data: RedemptionRequestData,
    ): Promise<{ success: boolean; data: RedemptionHistory; message: string }> {
        return this.post<{ success: boolean; data: RedemptionHistory; message: string }>(
            `${this.resource}/request-redeem`,
            data,
        );
    }

    /**
     * Redeems a reward at a specific branch.
     * @param data - Redeem reward data containing redemption_id and branch_id
     * @returns Promise with redemption history response
     */
    async redeem(
        data: RedeemRewardData,
    ): Promise<{ success: boolean; data: RedemptionHistory; message: string }> {
        return this.patch<{ success: boolean; data: RedemptionHistory; message: string }>(
            `${this.resource}/redeem`,
            data,
        );
    }

    /**
     * Redeems kaibigan points.
     * @param data - Points redemption data containing points amount
     * @returns Promise with redemption response
     */
    async redeemPoints(data: {
        points: number;
    }): Promise<{ success: boolean; data: KaibiganPointsResponse; message: string }> {
        return this.post<{ success: boolean; data: KaibiganPointsResponse; message: string }>(
            `${this.resource}/redeem-points`,
            data,
        );
    }

    /**
     * Gets recent redemption history (redeemed rewards).
     * @returns Promise with recent redeemed rewards response
     */
    async getRecentRedemptionHistory(): Promise<{
        success: boolean;
        data: LatestRedeemedReward[];
    }> {
        return this.get<{ success: boolean; data: LatestRedeemedReward[] }>(
            `${this.resource}/redeemed-rewards`,
        );
    }

    /**
     * Gets a single redemption detail by ID.
     * @param id - Redemption ID
     * @returns Promise with redemption detail response
     */
    async getRedemption(id: number): Promise<{ data: LatestRedeemedReward }> {
        return this.get<{ data: LatestRedeemedReward }>(`${this.resource}/redeemed/${id}`);
    }

    /**
     * Gets all redemption history with pagination.
     * @param params - Pagination parameters
     * @returns Promise with paginated redemption history response
     */
    async getAllRedemptionHistory(params?: PaginationParams): Promise<{
        data: RedemptionHistory[];
        links: PaginationLinks;
        meta: PaginationMeta;
    }> {
        return this.get<{
            data: RedemptionHistory[];
            links: PaginationLinks;
            meta: PaginationMeta;
        }>(`${this.resource}/redemption-history/view-all`, params);
    }

    /**
     * Submits a request to convert points to cash equivalent.
     * @param data - Convert points data containing points amount
     * @returns Promise with conversion response
     */
    async pointsConvert(
        data: ConvertPoints,
    ): Promise<{ success: boolean; data: ConvertPoints; message: string }> {
        return this.post<{ success: boolean; data: ConvertPoints; message: string }>(
            `${this.resource}/convert-points`,
            data,
        );
    }
}

export const rewardService = new RewardService();
export default RewardService;

// Re-export types for backward compatibility
export type {
    ConvertPoints,
    Customer,
    KaibiganPointsResponse,
    LatestRedeemedReward,
    RedeemRewardData,
    RedemptionHistory,
    RedemptionRequestData,
    Reward,
};
